import PropTypes from "prop-types";
import Img from "gatsby-image";
import { getFluidGatsbyImage } from "gatsby-storyblok-image";
import React, { RefObject } from "react";
import { StoryblokAsset, StoryblokAssetPropTypes } from "../../../interfaces/Storyblok/StoryblokAsset.interface";

export interface ImageProps {
  image?: StoryblokAsset | string;
  imageUrl?: string;
  maxWidth?: number;
  maxHeight?: number;
  /** Controls the max width used for fluid image generation (srcset/sizes). Higher values produce sharper images on large screens. Default: 800 */
  fluidMaxWidth?: number;
  /** Overrides the sizes attribute for responsive image selection. Use larger values on mobile for object-fit:cover images in tall containers. */
  fluidSizes?: string;
  objectFit?: "fill" | "contain" | "cover" | "scale-down" | "none";
  objectPosition?: "center" | "right" | "left" | "top" | "bottom";
  style?: string;
  filters?: string;
  ref?: RefObject<HTMLDivElement>;
  className?: string;
  isDecorative?: boolean;
}

const Image: React.FC<ImageProps> = ({
  image,
  imageUrl,
  maxWidth = undefined,
  maxHeight = undefined,
  fluidMaxWidth = undefined,
  fluidSizes = undefined,
  objectFit = "contain",
  objectPosition = "center",
  style = "",
  ref,
  className,
  isDecorative = false,
}) => {
  const imageExists = (image: StoryblokAsset | string): boolean => {
    if (typeof image === "string") {
      return false;
    }

    if (getImageAsStoryblokAsset(image)?.filename == null || getImageAsStoryblokAsset(image)?.filename == "") {
      return false;
    }

    return true;
  };

  const getImageAsStoryblokAsset = (image: StoryblokAsset | string): StoryblokAsset => {
    return image as StoryblokAsset;
  };

  const isSVG = (image: StoryblokAsset): boolean => {
    if (imageExists(image)) {
      const imageAsStoryblokAsset: StoryblokAsset = getImageAsStoryblokAsset(image);
      const fileExtensionRegex: RegExp = /\.[0-9a-z]+$/i;
      const potentialSVGFileExtensions: string[] = [".svg", ".SVG"];
      return potentialSVGFileExtensions.includes(
        imageAsStoryblokAsset.filename.match(fileExtensionRegex)?.length
          ? imageAsStoryblokAsset.filename.match(fileExtensionRegex)[0]
          : "",
      );
    }
    return false;
  };

  const fluidOptions = {
    quality: 90,
    ...(fluidMaxWidth ? { maxWidth: fluidMaxWidth } : {}),
    ...(fluidSizes ? { sizes: fluidSizes } : {}),
  };

  const fluidImage =
    getImageAsStoryblokAsset(image) &&
    getImageAsStoryblokAsset(image).filename &&
    getFluidGatsbyImage(getImageAsStoryblokAsset(image).filename, fluidOptions);

  const fluidImageFromUrl = imageUrl && getFluidGatsbyImage(imageUrl, fluidOptions);

  const styles = {
    width: "100%",
    maxWidth: maxWidth ? `${maxWidth}px` : "100%",
    maxHeight: maxHeight ? `${maxHeight}px` : "100%",
  };

  return (
    <>
      {imageExists(image) && (
        <>
          {isSVG(image as StoryblokAsset) ? (
            <img
              src={getImageAsStoryblokAsset(image).filename}
              alt={isDecorative ? "" : getImageAsStoryblokAsset(image).alt || "Icon"}
              role={isDecorative ? "presentation" : undefined}
              aria-hidden={isDecorative ? "true" : undefined}
              style={{
                objectFit: objectFit,
                objectPosition: "bottom",
                width: "100%",
                // height: '100%',
                maxWidth: maxWidth ? `${maxWidth}px` : "100%",
                maxHeight: maxHeight ? `${maxHeight}px` : "100%",
              }}
              loading={"lazy"}
            />
          ) : (
            fluidImage && (
              <Img
                fluid={fluidImage}
                alt={
                  isDecorative
                    ? ""
                    : getImageAsStoryblokAsset(image) && getImageAsStoryblokAsset(image).alt
                      ? getImageAsStoryblokAsset(image).alt
                      : "Tile image"
                }
                role={isDecorative ? "presentation" : undefined}
                aria-hidden={isDecorative ? "true" : undefined}
                style={{
                  width: "100%",
                  maxWidth: maxWidth,
                  height: "100%",
                  maxHeight: maxHeight,
                }}
                imgStyle={{
                  objectFit: objectFit,
                  objectPosition: objectPosition,
                  width: "100%",
                  maxWidth: maxWidth,
                  maxHeight: maxHeight,
                }}
                loading={"auto"}
                ref={ref}
                className={className}
              />
            )
          )}
        </>
      )}
      {imageUrl && (
        <Img
          fluid={fluidImageFromUrl}
          alt={isDecorative ? "" : "Image"}
          role={isDecorative ? "presentation" : undefined}
          aria-hidden={isDecorative ? "true" : undefined}
          style={{
            width: "100%",
            maxWidth: maxWidth,
            height: "100%",
            maxHeight: maxHeight,
          }}
          imgStyle={{
            objectFit: objectFit,
            objectPosition: objectPosition,
            width: "100%",
            maxWidth: maxWidth,
            maxHeight: maxHeight,
          }}
          loading={"auto"}
          ref={ref}
          className={className}
        />
      )}
    </>
  );
};

export default Image;
